import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Chip,
  Alert,
  Card,
  CardContent,
  alpha,
  useTheme,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { printPayment } from '../../../utils/printVoucher';

interface IAccount {
  id: string;
  name: string;
  accountNumber?: string;
}

interface IPayment {
  id: string;
  paymentNumber: string;
  date: string;
  amount: number;
  debitAccount: IAccount;
  creditAccount: IAccount;
  description: string;
  status: string;
  createdBy: string;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
  supplier?: string;
  branchId?: string;
  branchName?: string;
}

interface PaymentViewProps {
  paymentId: string | null;
  open: boolean;
  onClose: () => void;
}

const PaymentView: React.FC<PaymentViewProps> = ({ paymentId, open, onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [payment, setPayment] = useState<IPayment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && paymentId) {
      fetchPayment();
    } else if (!open) {
      setPayment(null);
      setError(null);
    }
  }, [paymentId, open]);

  const fetchPayment = async () => {
    if (!paymentId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/finance/payments/${paymentId}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token || ''
        }
      });
      
      if (!response.ok) {
        throw new Error('فشل جلب بيانات السند');
      }
      
      const paymentData = await response.json();
      
      const formattedPayment: IPayment = {
        id: paymentData.id || paymentData._id || paymentId,
        paymentNumber: paymentData.paymentNumber,
        date: paymentData.date,
        amount: paymentData.amount,
        debitAccount: paymentData.debitAccount || { id: '', name: 'غير محدد' },
        creditAccount: paymentData.creditAccount || { id: '', name: 'غير محدد' },
        description: paymentData.description,
        status: paymentData.status,
        createdBy: paymentData.createdBy,
        approvedBy: paymentData.approvedBy,
        approvedAt: paymentData.approvedAt,
        createdAt: paymentData.createdAt,
        supplier: paymentData.supplier,
        branchId: paymentData.branchId,
        branchName: paymentData.branchName
      };
      
      setPayment(formattedPayment);
    } catch (err: any) {
      console.error('خطأ في جلب سند الصرف:', err);
      setError(err.message || 'حدث خطأ أثناء جلب بيانات السند');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    if (payment) {
      try {
        await printPayment(payment.id);
      } catch (error) {
        console.error('خطأ في طباعة السند:', error);
      }
    }
  };

  const handleEdit = () => {
    if (payment) {
      onClose();
      navigate(`/finance/payments/edit/${payment.id}`);
    }
  };

  const renderStatus = (status: string) => {
    let color: 'success' | 'warning' | 'error' | 'default' = 'default';
    let icon;
    
    switch (status) {
      case 'مؤكد':
        color = 'success';
        icon = <CheckCircleIcon fontSize="small" />;
        break;
      case 'تحت الموافقة':
        color = 'warning';
        break;
      case 'مرفوض':
        color = 'error';
        icon = <CancelIcon fontSize="small" />;
        break;
    }
    
    return (
      <Chip 
        size="medium" 
        label={status}
        color={color}
        icon={icon}
        sx={{ 
          fontWeight: 600,
          fontSize: '0.875rem'
        }}
      />
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: alpha('#30c3b0', 0.05),
        borderBottom: '1px solid',
        borderColor: alpha('#000', 0.08)
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PaymentIcon sx={{ mr: 1, color: '#30c3b0', fontSize: 32 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              سند صرف
            </Typography>
            {payment && (
              <Typography variant="body2" color="text.secondary">
                {payment.paymentNumber}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress size={50} sx={{ color: '#30c3b0' }} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !payment ? (
          <Alert severity="warning">السند غير موجود</Alert>
        ) : (
          <Box>
            {/* معلومات السند الأساسية */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 2, color: '#30c3b0', fontWeight: 600 }}>
                      معلومات السند
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                          التاريخ
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Date(payment.date).toLocaleDateString('ar-SA', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          الحالة
                        </Typography>
                        {renderStatus(payment.status)}
                      </Box>
                      
                      {payment.branchName && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            الفرع
                          </Typography>
                          <Typography variant="body1">
                            {payment.branchName}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: alpha('#f44336', 0.05) }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 2, color: '#30c3b0', fontWeight: 600 }}>
                      <AttachMoneyIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                      المبلغ
                    </Typography>
                    
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.error.main, mb: 2 }}>
                      {payment.amount.toLocaleString('ar-SA')} ر.س
                    </Typography>
                    
                    {payment.supplier && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          <PersonIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                          المورد
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {payment.supplier}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* تفاصيل الحسابات */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2, color: '#30c3b0', fontWeight: 600 }}>
                  <AccountBalanceIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  تفاصيل الحسابات
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, bgcolor: alpha(theme.palette.error.main, 0.05), borderRadius: 1, border: '1px solid', borderColor: alpha(theme.palette.error.main, 0.2) }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        الحساب المدين (من)
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {payment.debitAccount.name}
                      </Typography>
                      {payment.debitAccount.accountNumber && (
                        <Typography variant="caption" color="text.secondary">
                          رقم الحساب: {payment.debitAccount.accountNumber}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1, border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.2) }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        الحساب الدائن (إلى)
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {payment.creditAccount.name}
                      </Typography>
                      {payment.creditAccount.accountNumber && (
                        <Typography variant="caption" color="text.secondary">
                          رقم الحساب: {payment.creditAccount.accountNumber}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* البيان */}
            {payment.description && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 1, color: '#30c3b0', fontWeight: 600 }}>
                    <DescriptionIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    البيان
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                    {payment.description}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* معلومات الإنشاء والاعتماد */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2, color: '#30c3b0', fontWeight: 600 }}>
                  معلومات إضافية
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      أنشئ بواسطة
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {payment.createdBy}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      تاريخ الإنشاء
                    </Typography>
                    <Typography variant="body2">
                      {new Date(payment.createdAt).toLocaleString('ar-SA')}
                    </Typography>
                  </Grid>
                  
                  {payment.approvedBy && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          اعتمد بواسطة
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {payment.approvedBy}
                        </Typography>
                      </Grid>
                      
                      {payment.approvedAt && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            تاريخ الاعتماد
                          </Typography>
                          <Typography variant="body2">
                            {new Date(payment.approvedAt).toLocaleString('ar-SA')}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: alpha('#000', 0.02) }}>
        <Button onClick={onClose} variant="outlined">
          إغلاق
        </Button>
        {payment && payment.status !== 'مؤكد' && (
          <Button 
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ color: '#30c3b0', borderColor: '#30c3b0' }}
          >
            تعديل
          </Button>
        )}
        {payment && (
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              bgcolor: '#30c3b0',
              '&:hover': { bgcolor: '#25a899' }
            }}
          >
            طباعة
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentView;